import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Cpu, Film, Images, Info, Search, Sparkles, Wand2, Eraser } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { AdminAiUsage } from "@/components/AdminAiUsage";
import { AdminApiQuotas } from "@/components/AdminApiQuotas";
import { AdminMarketMemory } from "@/components/AdminMarketMemory";
import { Input } from "@/components/ui/input";
import { PLANS, PLAN_ORDER } from "@/lib/plans";
import { PHOTO_SCAN_PER_DAY } from "@/lib/photo-scan";
import {
  AI_OPERATIONS,
  DEFAULT_CREDIT_SEK,
  MODEL_RATES,
  creditsForOperation,
  formatSek,
} from "@/lib/ai-costs";

export const Route = createFileRoute("/_authenticated/admin/ai-kostnader")({
  head: () => ({
    meta: [
      { title: "AI-kostnader — Administration" },
      {
        name: "description",
        content: "Intern kalkyl: vad AI-annonser och AI-sökningar kostar per anrop och per plan.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="AI-kostnader">
      <div className="space-y-10">
        <AdminApiQuotas />
        <AdminAiUsage />
        <AdminMarketMemory compact />
        <CostCalculator />
      </div>
    </AdminShell>
  ),
});

const OP_ICONS: Record<string, typeof Sparkles> = {
  ad: Sparkles,
  search: Search,
  video_scan_part: Film,
  video_scan_extra: Film,
  photo_scan: Images,
  clean_image: Eraser,
  rewrite: Wand2,
};

/** Hur många skanningar per dygn respektive plan får göra. */
const VIDEO_SCANS_PER_DAY: Record<string, number> = { free: 0, plus: 0, pro: 40, expert: 60 };
/** Antal delar en typisk film delas upp i. */
const VIDEO_PARTS_PER_SCAN = 2;

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2 last:border-0">
      <span className={muted ? "text-sm text-muted-foreground" : "text-sm text-foreground"}>
        {label}
      </span>
      <span className="font-heading text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}

function CostCalculator() {
  const [creditSek, setCreditSek] = useState(DEFAULT_CREDIT_SEK);
  /** Hur stor andel av kvoten en aktiv användare faktiskt utnyttjar. */
  const [utilisation, setUtilisation] = useState(35);

  const perOp = useMemo(
    () =>
      AI_OPERATIONS.map((op) => {
        const credits = creditsForOperation(op);
        return { op, credits, sek: credits * creditSek };
      }),
    [creditSek],
  );

  const adCost = perOp.find((p) => p.op.id === "ad")!;
  const searchCost = perOp.find((p) => p.op.id === "search")!;
  const videoPartCost = perOp.find((p) => p.op.id === "video_scan_part")!;
  const videoExtraCost = perOp.find((p) => p.op.id === "video_scan_extra")!;
  const photoScanCost = perOp.find((p) => p.op.id === "photo_scan")!;
  const videoScanSek = videoPartCost.sek * VIDEO_PARTS_PER_SCAN + videoExtraCost.sek;

  const planRows = PLAN_ORDER.map((id) => {
    const plan = PLANS[id];
    const l = plan.limits;
    const maxAds = l.perMonth ?? Math.min((l.perDay ?? 0) * 30, (l.perWeek ?? 0) * 4.3);
    const maxSearches = l.aiSearchPerDay * 30;
    const maxVideoScans = (VIDEO_SCANS_PER_DAY[plan.id] ?? 0) * 30;
    const maxPhotoScans = (PHOTO_SCAN_PER_DAY[plan.id] ?? 0) * 30;
    const maxScans = maxVideoScans + maxPhotoScans;
    const scanSek = maxVideoScans * videoScanSek + maxPhotoScans * photoScanCost.sek;
    const maxSek = maxAds * adCost.sek + maxSearches * searchCost.sek + scanSek;
    const realSek = maxSek * (utilisation / 100);
    return { plan, maxAds, maxSearches, maxScans, maxSek, realSek };
  });

  return (
    <div className="space-y-10">
      <section className="rounded-md border border-border bg-card p-5">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold">
          <Cpu className="h-5 w-5 text-primary" /> Uppmätt modellpris
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Härlett ur riktiga anrop i AI Gateway-loggen genom att lösa ut kostnaden mot
          token-antalen. Modellen som används i produktion är{" "}
          <strong>google/gemini-3.6-flash</strong>.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {Object.entries(MODEL_RATES).map(([model, rate]) => (
            <div key={model} className="rounded-md border border-border p-4">
              <p className="font-mono text-xs text-muted-foreground">{model}</p>
              <Row label="Krediter / 1M tokens in" value={String(rate.inputPerMillion)} />
              <Row label="Krediter / 1M tokens ut" value={String(rate.outputPerMillion)} />
              <Row
                label="≈ kr / 1M tokens ut"
                value={formatSek(rate.outputPerMillion * creditSek, 0)}
                muted
              />
            </div>
          ))}
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="text-sm">
            <span className="font-medium text-foreground">Kreditpris (kr per Lovable-kredit)</span>
            <Input
              type="number"
              step="0.1"
              min="0"
              value={creditSek}
              onChange={(e) => setCreditSek(Number(e.target.value) || 0)}
              className="mt-1"
            />
          </label>
          <label className="text-sm">
            <span className="font-medium text-foreground">Antaget kvotutnyttjande (%)</span>
            <Input
              type="number"
              min="1"
              max="100"
              value={utilisation}
              onChange={(e) => setUtilisation(Number(e.target.value) || 1)}
              className="mt-1"
            />
          </label>
        </div>
      </section>

      <section>
        <h2 className="font-heading text-lg font-semibold">Kostnad per AI-anrop</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {perOp.map(({ op, credits, sek }) => (
            <div key={op.id} className="rounded-md border border-border bg-card p-4">
              <p className="flex items-center gap-2 font-heading font-semibold text-foreground">
                {(() => {
                  const Icon = OP_ICONS[op.id] ?? Sparkles;
                  return <Icon className="h-4 w-4 text-primary" />;
                })()}
                {op.name}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{op.description}</p>
              <div className="mt-3">
                <Row label="Tokens in / ut" value={`${op.inputTokens} / ${op.outputTokens}`} />
                <Row label="Krediter per anrop" value={credits.toFixed(5)} />
                <Row label="Kostnad per anrop" value={formatSek(sek, 3)} />
                <Row label="Kostnad per 1 000 anrop" value={formatSek(sek * 1000, 0)} />
              </div>
              <p className="mt-3 flex gap-2 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {op.source}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-lg font-semibold">Kostnad och marginal per plan</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          «Tak» = användaren maxar varje kvot varje månad. «Realistiskt» = {utilisation} % av taket.
          Intäkten räknas på månadspriset; vid årsbetalning motsvarar det{" "}
          {(PLANS.plus.priceYearSek / 12).toFixed(0)} kr/mån för Plus och{" "}
          {(PLANS.pro.priceYearSek / 12).toFixed(0)} kr/mån för Pro.
        </p>
        <div className="mt-4 overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/60 text-left">
              <tr>
                <th className="whitespace-nowrap px-3 py-2 font-semibold">Plan</th>
                <th className="whitespace-nowrap px-3 py-2 font-semibold">Intäkt/mån</th>
                <th className="whitespace-nowrap px-3 py-2 font-semibold">Annonser (tak)</th>
                <th className="whitespace-nowrap px-3 py-2 font-semibold">AI-sök (tak)</th>
                <th className="whitespace-nowrap px-3 py-2 font-semibold">Skanningar (tak)</th>
                <th className="whitespace-nowrap px-3 py-2 font-semibold">AI-kostnad tak</th>
                <th className="whitespace-nowrap px-3 py-2 font-semibold">AI-kostnad realistiskt</th>
                <th className="whitespace-nowrap px-3 py-2 font-semibold">Marginal (realistiskt)</th>
              </tr>
            </thead>
            <tbody>
              {planRows.map(({ plan, maxAds, maxSearches, maxScans, maxSek, realSek }) => {
                const margin = plan.priceSek - realSek;
                return (
                  <tr key={plan.id} className="border-t border-border">
                    <td className="whitespace-nowrap px-3 py-2 font-medium">{plan.name}</td>
                    <td className="whitespace-nowrap px-3 py-2">{plan.priceSek} kr</td>
                    <td className="whitespace-nowrap px-3 py-2">{Math.round(maxAds)}</td>
                    <td className="whitespace-nowrap px-3 py-2">{maxSearches}</td>
                    <td className="whitespace-nowrap px-3 py-2">{maxScans}</td>
                    <td className="whitespace-nowrap px-3 py-2">{formatSek(maxSek)}</td>
                    <td className="whitespace-nowrap px-3 py-2">{formatSek(realSek)}</td>
                    <td
                      className={`px-3 py-2 font-semibold ${margin < 0 ? "text-destructive" : "text-primary"}`}
                    >
                      {formatSek(margin)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Siffrorna täcker bara modellkostnaden. Lagring av bilder, bandbredd och drift tillkommer.
        </p>
      </section>

      <section className="rounded-md border border-border bg-card p-5">
        <h2 className="font-heading text-lg font-semibold">Slutsats</h2>
        <ul className="mt-3 space-y-2 text-sm text-foreground/85">
          <li>
            En AI-annons kostar ca <strong>{formatSek(adCost.sek, 3)}</strong> – vi kan alltså vara
            betydligt mer frikostiga med antalet annonser än vad kvoterna säger idag.
          </li>
          <li>
            En AI-sökning kostar ca <strong>{formatSek(searchCost.sek, 3)}</strong>. Även 200
            sökningar per dygn landar på {formatSek(searchCost.sek * 200 * 30)} i månaden.
          </li>
          <li>
            En videoskanning kostar ca <strong>{formatSek(videoScanSek, 3)}</strong> (film upp till
            100 MB delas i {VIDEO_PARTS_PER_SCAN} delar plus två uppföljningsanrop) och en
            bulk-fotoskanning ca <strong>{formatSek(photoScanCost.sek, 3)}</strong>. Skanningarna är
            dyrast per anrop – men de är också det som ger flest annonser per användare.
          </li>
          <li>
            Den stora kostnadsdrivaren är bilderna i annonsflödet (tokens in) och den genererade
            texten (tokens ut) – inte sökningarna.
          </li>
        </ul>
      </section>
    </div>
  );
}
